/**
 * Engine Atlas 3×3 grid container per Atlas Day 2 visual-layout commitment
 * A.1 + (a) + D.
 *
 * Implements the locked 3×3 geography (3 altitudes × 3 stages) as a 4-column
 * × 4-row CSS Grid:
 *   - Column 1: axis labels (vertical-orientation: row labels in left margin)
 *   - Columns 2-4: stage cells (input / calculation / decision)
 *   - Row 1: axis labels (horizontal-orientation: column labels in top margin)
 *   - Rows 2-4: altitude rows (position / transformation / substrate)
 *
 * Variable-height rows per A.1 — grid rows take max height per row based on
 * occupancy. Position row collapses to the height of its PositionRowCell
 * children (h-8 ≈ 32px) per E.1. Transformation + substrate rows take
 * content-driven heights based on RegionContainer / cluster / flat-region
 * content depth.
 *
 * Container width per (a): breaks the title-block max-w-5xl constraint;
 * grid section renders at max-w-7xl (specific width per build-time refinement).
 * Container width is the parent's responsibility — AtlasGrid only provides
 * grid structure.
 *
 * Children placement: caller emits children in 4×4 row-major order (16
 * grid children total). Top-left corner is empty (axis-label-row × axis-
 * label-column intersection). AtlasGrid does not enforce children shape;
 * children are positioned by document order via implicit grid placement.
 *
 * Convention-fit drift-check (visual-layout commitment discipline): this
 * component refuses the dominant arbi vertical-anchored-sections pattern
 * even though Settings.tsx is the canonical Type 3. Atlas geography lives
 * at A.1 grid altitude; sections-pattern lives at Settings.
 */

import type { JSX, ReactNode } from 'react';

export interface AtlasGridProps {
  children: ReactNode;
}

export default function AtlasGrid({ children }: AtlasGridProps): JSX.Element {
  return (
    <div
      data-atlas-grid=""
      className="grid grid-cols-[minmax(100px,auto)_1fr_1fr_1fr] gap-2"
    >
      {children}
    </div>
  );
}
